import { useState, useCallback } from 'react';
import type { MouseEvent } from 'react';
import {
  IconButton,
  Popover,
  Box,
  Typography,
  TextField,
  Button,
  Badge,
  CircularProgress,
} from '@mui/material';
import { Security as SecurityIcon } from '@mui/icons-material';
import {
  useGetPasswordStatusQuery,
  useSetPasswordMutation,
  useClearPasswordMutation,
} from '../api/sensitiveDataApi';
import { useSensitiveDataPassword } from '../hooks/useSensitiveDataPassword';

/**
 * Toolbar button that lets users enter their sensitive data token.
 * When set, the backend cookie enables AES-decryption of protected fields in grids/detail forms.
 * An orange dot badge on the icon indicates that the token is currently active.
 */
export const SensitiveDataButton = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { password, setPassword, validation, reset } = useSensitiveDataPassword();

  const { data: status, isLoading: isStatusLoading } = useGetPasswordStatusQuery();
  const [setPasswordMutation, { isLoading: isSetting }] = useSetPasswordMutation();
  const [clearPasswordMutation, { isLoading: isClearing }] = useClearPasswordMutation();

  const isOpen = Boolean(anchorEl);
  const isSet = status?.isSet ?? false;
  const isMutating = isSetting || isClearing;

  const handleOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    reset();
  }, [reset]);

  const handleSet = useCallback(async () => {
    if (!validation.isValid) return;
    await setPasswordMutation({ Password: password }).unwrap();
    handleClose();
  }, [password, validation.isValid, setPasswordMutation, handleClose]);

  const handleClear = useCallback(async () => {
    await clearPasswordMutation().unwrap();
    handleClose();
  }, [clearPasswordMutation, handleClose]);

  return (
    <>
      <IconButton
        color="inherit"
        aria-label="sensitive data token"
        aria-expanded={isOpen}
        onClick={handleOpen}
        disabled={isStatusLoading}
      >
        <Badge color="warning" variant="dot" invisible={!isSet}>
          <SecurityIcon />
        </Badge>
      </IconButton>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableRestoreFocus
      >
        <Box sx={{ p: 2, width: 280 }}>
          <Typography variant="subtitle2" gutterBottom>
            Sensitive Data Token
          </Typography>

          {isSet ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Token is active. Sensitive fields are currently visible.
              </Typography>
              <Button
                variant="contained"
                color="error"
                fullWidth
                onClick={handleClear}
                disabled={isMutating}
                startIcon={isClearing ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                Clear Token
              </Button>
            </>
          ) : (
            <>
              <TextField
                label="Password"
                type="password"
                size="small"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && validation.isValid) handleSet();
                }}
                sx={{ mb: 1 }}
                autoComplete="new-password"
                autoFocus
              />
              {password.length > 0 && !validation.isValid && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                  Must contain at least one number and one uppercase and lowercase letter
                </Typography>
              )}
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleSet}
                disabled={!validation.isValid || isMutating}
                startIcon={isSetting ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                Set Token
              </Button>
            </>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default SensitiveDataButton;
